/**
 * Cart Icon Component
 *
 * Shopping cart icon with notification badge.
 * Figma: Cart Icon (Node: 1:2911)
 *
 * Dimensions: 31.499px × 31.499px container
 * Badge: 17.495px circle with count
 * Colors: Badge bg #030213 (stroke.strongBlack), text white
 * Typography: Inter Medium, 10.5px, line-height 14px (heading5)
 */
"use client";

import { forwardRef } from "react";

import Image from "next/image";

import { colors } from "@/styles/design-tokens";

type CartIconSize = "default" | "mobile";

interface CartIconProps {
  count?: number;
  size?: CartIconSize;
  className?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const BASE_DIMENSIONS = {
  container: 31.5,
  icon: 16,
  badge: 17.495,
  badgeOffsetLeft: 16,
  badgeOffsetTop: -3.49,
} as const;

const CART_ICON_SIZES: Record<CartIconSize, number> = {
  default: BASE_DIMENSIONS.container,
  mobile: 40,
};

export const CartIcon = forwardRef<HTMLButtonElement, CartIconProps>(function CartIcon(
  { count = 0, size = "default", className = "", onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const containerSize = CART_ICON_SIZES[size];
  const scale = containerSize / BASE_DIMENSIONS.container;
  const iconSize = BASE_DIMENSIONS.icon * scale;
  const badgeSize = BASE_DIMENSIONS.badge * scale;
  const badgeOffsetLeft = BASE_DIMENSIONS.badgeOffsetLeft * scale;
  const badgeOffsetTop = BASE_DIMENSIONS.badgeOffsetTop * scale;

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`relative flex items-center justify-center rounded-full transition-opacity hover:opacity-80 ${className}`}
      style={{
        width: `${containerSize}px`,
        height: `${containerSize}px`,
      }}
      aria-label={`Shopping cart${count > 0 ? ` with ${count} items` : ""}`}
    >
      {/* Cart Icon */}
      <Image src="/icons/cart.svg" alt="" width={iconSize} height={iconSize} className="relative" />

      {/* Notification Badge */}
      {count > 0 && (
        <div
          className="absolute flex items-center justify-center rounded-full"
          style={{
            backgroundColor: colors.stroke.strongBlack,
            width: `${badgeSize}px`,
            height: `${badgeSize}px`,
            left: `${badgeOffsetLeft}px`,
            top: `${badgeOffsetTop}px`,
          }}
        >
          <span className="text-caption text-white">{count > 9 ? "9+" : count}</span>
        </div>
      )}
    </button>
  );
});
